///
/// EarthBrowser
///
/// Created by Matt Giger on 9/16/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Canonical client-side entitlement resolution for the Phase 4 depth gates
/// (documentation/monetization/SUBSCRIPTION_PRICING_MODEL.md): paid means any
/// active/trialing `yearly` or `beta_yearly` subscription row. The tenure
/// full-access grant writes tier `yearly` so it is honored automatically.
/// Anonymous and still-loading users resolve to free-tier depth so gates
/// never flicker open before the subscription query settles.

import { setPaidEntitlement } from '@/redux/entitlementsSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { useGetUserSubscriptionQuery } from '@/redux/userApi'
import { clearTileToken } from '@/services/tiles/TileTokenService'
import type { Subscription } from '@shared-types/subscription'
import { useEffect } from 'react'

// Depth limits live in a dependency-free module so engine code (Clock) can
// import them without pulling in this file's redux/RTK-query graph;
// re-exported here for the React-side consumers
import { cFreeForecastDays, cFreeHistoryHours, cPaidForecastDays, cPaidHistoryHours } from '@/utils/entitlementLimits'
export { cFreeForecastDays, cFreeHistoryHours, cPaidForecastDays, cPaidHistoryHours }

/// The subscription row that grants paid entitlements, if any
export function findPaidSubscription(subscriptions: Subscription[] | undefined): Subscription | undefined {
	return subscriptions?.find(subscription =>
		(subscription.status === 'active' || subscription.status === 'trialing') &&
		(subscription.subscription_id === 'yearly' || subscription.subscription_id === 'beta_yearly')
	)
}

export function isPaidSubscription(subscriptions: Subscription[] | undefined): boolean {
	return findPaidSubscription(subscriptions) !== undefined
}

export interface Entitlements {
	isPaid: boolean
	forecastDays: number
	historyHours: number
	// True while the signed-in user's subscription is still being fetched;
	// free-tier values are returned in the meantime
	isLoading: boolean
}

export function useEntitlements(): Entitlements {
	const userProfile = useAppSelector(state => state.user.userProfile)
	const { data: subscriptions, isLoading } = useGetUserSubscriptionQuery(undefined, { skip: !userProfile })

	const isPaid = !!userProfile && isPaidSubscription(subscriptions)
	return {
		isPaid,
		forecastDays: isPaid ? cPaidForecastDays : cFreeForecastDays,
		historyHours: isPaid ? cPaidHistoryHours : cFreeHistoryHours,
		isLoading: !!userProfile && isLoading,
	}
}

/// Mirrors the resolved entitlement into the non-persisted entitlements
/// slice for non-React consumers (dataset layers). Mount once in PageContent.
export function useEntitlementsSync(): void {
	const dispatch = useAppDispatch()
	const { isPaid } = useEntitlements()
	const stored = useAppSelector(state => state.entitlements.isPaid)

	useEffect(() => {
		if (stored !== isPaid) {
			dispatch(setPaidEntitlement(isPaid))
			// Tile tokens carry the access tier as a claim, so a mid-session
			// entitlement change (checkout completes, subscription lapses)
			// must drop the current token: the next tile request mints one
			// with the new tier instead of waiting out the 30-minute expiry
			clearTileToken()
		}
	}, [isPaid, stored, dispatch])
}
